import { Dispatch, FC, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, Typography, useTheme } from '@mui/material';

import { EmptyStarIcon, FilledStartIcon } from '../../icons';

import { StyledStack } from './styles';

const STARS = [1, 2, 3, 4, 5] as const;

export type TRating = 0 | typeof STARS[number];

type TSetter = Dispatch<SetStateAction<TRating>>;
type THandler = (newRating: TRating) => void;

interface IMplRatingProps {
	/**
	 * @param onRatingChange is callback that triggers when the rating changes.
	 * Can be either a state updater function or a custom handler.
	 */
	value: TRating;
	onRatingChange?: TSetter | THandler;
	starSize?: number;
	suggestionPosition?: 'right' | 'bottom';
	disabled?: boolean;
}

export const MplRating: FC<IMplRatingProps> = ({
	value,
	onRatingChange,
	starSize,
	suggestionPosition,
	disabled = false,
}) => {
	const { typography } = useTheme();

	const { t } = useTranslation('booking');

	const onClickHandler = (star: TRating) => {
		!disabled && onRatingChange && onRatingChange(star);
	};

	const hasRightSuggestion = suggestionPosition === 'right';
	const hasBottomSuggestion = suggestionPosition === 'bottom';
	const hasRating = value !== 0;
	const suggestion = t(`feedback.rating_${value}`);

	return (
		<Stack width="100%" gap="2px">
			<Stack width="100%" direction="row" alignItems="center" gap={1}>
				<Stack
					direction="row"
					justifyContent="space-between"
					gap={1}
					flexBasis={hasRightSuggestion ? '50%' : '100%'}
				>
					{STARS.map((star) => (
						<StyledStack
							key={star}
							starSize={starSize}
							filled={star > value}
							onClick={() => onClickHandler(star)}
							disabled={disabled}
						>
							{star > value ? <EmptyStarIcon /> : <FilledStartIcon />}
						</StyledStack>
					))}
				</Stack>
				{hasRightSuggestion && (
					<Typography
						children={hasRating ? `/ ${suggestion}` : ''}
						variant="body2"
						color="neutral.80"
					/>
				)}
			</Stack>
			{hasBottomSuggestion && (
				<Typography
					width="100%"
					variant="caption"
					color="neutral.80"
					textAlign="center"
					minHeight={typography.caption.lineHeight}
					children={hasRating ? `(${suggestion})` : ''}
				/>
			)}
		</Stack>
	);
};
